import React, {FC, ReactNode} from 'react';
import {connect} from 'react-redux';

import {ResolveDispatchProps} from '../../../../../../../../src/helpers/reduxHelpers';
import {CardGroupModel} from '../../../../../../../../src/models/api/cardGroupModel';
import {
  makeGetContactsManagerTableOffsetPaginationData,
  makeGetContactsManagerTableState,
  makeGetContactsTableCardIds,
  makeGetIsInMultiSelectMode,
  makeGetRouteCardId,
  makeGetSelectedCardIds,
} from '../../../../../../../../src/store/panels/contacts/contactsManager/contactsManagerPanelSelectors';
import {
  contactsManagerFetchListContactsTableDataAsyncAction,
  contactsManagerSetUpContactsTableAction,
  contactsManagerTableAddSelectedCardIdsAction,
  contactsManagerTableCleanupAction,
  contactsManagerTableRemoveSelectedCardIdsAction,
} from '../../../../../../../../src/store/panels/contacts/contactsManager/table/contactsManagerTableActions';
import {tableStateIsContactsTableState} from '../../../../../../../../src/store/panels/contacts/contactsManager/table/contactsManagerTableState';
import {ContactsManagerPanelId} from '../../../../../../../../src/store/panels/panelStateBase';
import {
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
  ResolveStateProps,
} from '../../../../../../../../srcCommon/helpers/reselectHelpers';
import {useInitialEffect} from '../../../../../../../shared/helpers/src/react/hooks/useEffectHelpers';
import {Renderer} from '../../../../../../../shared/helpers/src/react/reactHelpers';
import {CancelToken} from '../../../../../../../shared/helpers/src/utilities/asyncCancelHelpers';
import {makeGetDynamicColumns} from '../common/tableSelectors';
import {ListContactsTable} from './listContactsTable';

/*
 * Props.
 */

interface ListContactsTableContainerPublicProps {
  panelId: ContactsManagerPanelId;
  multiSelectHeaderActions: ReactNode;
  renderEmpty: Renderer;
  cardGroup: CardGroupModel;
  shouldHideAccountsColumn?: boolean;
}
type ListContactsTableContainerStateProps = ResolveStateProps<typeof mapStateToProps>;
type ListContactsTableContainerDispatchProps = ResolveDispatchProps<typeof mapDispatchToProps>;
type ListContactsTableContainerComponentProps = ListContactsTableContainerPublicProps &
  ListContactsTableContainerStateProps &
  ListContactsTableContainerDispatchProps;

/*
 * Selectors.
 */

function makeGetListContactsTableStatus(
  getContactsManagerTableState: ReturnType<typeof makeGetContactsManagerTableState>,
) {
  return createSelector([getContactsManagerTableState], (tableState) => {
    if (!tableState || !tableStateIsContactsTableState(tableState)) {
      return undefined;
    }
    return tableState.status;
  });
}

/*
 * Component.
 */

export const ListContactsTableContainerComponent: FC<ListContactsTableContainerComponentProps> = (props) => {
  const {
    cardIds,
    multiSelectHeaderActions,
    shouldHideAccountsColumn,
    isInMultiSelectMode,
    selectedCardIds,
    tableStatus,
    dynamicColumns,
    cardGroup,
    paginationData,
    routeCardId,
    onFetchTableDataAsync,
    renderEmpty,
    setUpContactsTable,
    cleanup,
    addSelectedCardIds,
    removeSelectedCardIds,
  } = props;

  useInitialEffect(() => {
    // We may have preselected card from the route.
    setUpContactsTable(routeCardId ? [routeCardId] : []);
    // Listen for the component to be unmounted and clean it up.
    return () => {
      cleanup();
    };
  });

  const onReset = () => {
    cleanup();
    setUpContactsTable();
  };

  // The table can't fetch until it's set up in Redux.
  if (tableStatus === undefined) {
    return null;
  }

  return (
    <ListContactsTable
      cardIds={cardIds}
      cardGroup={cardGroup}
      shouldHideAccountsColumn={shouldHideAccountsColumn}
      multiSelectHeaderActions={multiSelectHeaderActions}
      dynamicColumns={dynamicColumns}
      isInMultiSelectMode={isInMultiSelectMode}
      selectedCardIds={selectedCardIds}
      tableStatus={tableStatus}
      paginationData={paginationData}
      onFetchTableDataAsync={onFetchTableDataAsync}
      renderEmpty={renderEmpty}
      onReset={onReset}
      addSelectedCardIds={addSelectedCardIds}
      removeSelectedCardIds={removeSelectedCardIds}
    />
  );
};

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<ListContactsTableContainerPublicProps>()(() => {
  const getContactsManagerTableState = makeGetContactsManagerTableState();
  return {
    cardIds: makeGetContactsTableCardIds(),
    tableStatus: makeGetListContactsTableStatus(getContactsManagerTableState),
    isInMultiSelectMode: makeGetIsInMultiSelectMode(),
    selectedCardIds: makeGetSelectedCardIds(),
    dynamicColumns: makeGetDynamicColumns(),
    paginationData: makeGetContactsManagerTableOffsetPaginationData(getContactsManagerTableState),
    routeCardId: makeGetRouteCardId(),
  };
});

const mapDispatchToProps = makeMapDispatchToProps<ListContactsTableContainerPublicProps>()(
  ({panelId, cardGroup}) => ({
    setUpContactsTable: contactsManagerSetUpContactsTableAction,
    cleanup: contactsManagerTableCleanupAction,
    addSelectedCardIds: contactsManagerTableAddSelectedCardIdsAction,
    removeSelectedCardIds: contactsManagerTableRemoveSelectedCardIdsAction,
    onFetchTableDataAsync: (cancelToken: CancelToken) =>
      contactsManagerFetchListContactsTableDataAsyncAction(panelId, cardGroup.id, cancelToken),
  }),
);

export const ListContactsTableContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(ListContactsTableContainerComponent);
